import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  ArrowLeft,
  Star,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  Phone,
  Mail,
  FileText,
  Shield,
  TrendingDown,
  Award,
  MessageSquare,
  Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuoteStatusBadge = ({ status }) => {
  const statusConfig = {
    submitted: { label: 'New', className: 'bg-blue-100 text-blue-700' },
    shortlisted: { label: 'Shortlisted', className: 'bg-yellow-100 text-yellow-700' },
    accepted: { label: 'Accepted', className: 'bg-green-100 text-green-700' },
    rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
    not_selected: { label: 'Not Selected', className: 'bg-gray-100 text-gray-600' },
  };
  
  const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
  return <Badge className={config.className}>{config.label}</Badge>;
};

const DeliveryTimeBadge = ({ time }) => {
  const timeLabels = {
    same_day: 'Same Day',
    '1_3_days': '1-3 Days',
    '1_week': '1 Week',
    '2_weeks': '2 Weeks',
    custom: 'Custom',
  };
  
  return (
    <span className="flex items-center gap-1 text-sm">
      <Truck className="w-4 h-4" />
      {timeLabels[time] || time}
    </span>
  );
};

const QuoteCard = ({ 
  quote, 
  currencySymbol, 
  isSelected, 
  onToggleSelect, 
  onShortlist, 
  onAccept, 
  onReject,
  rfqStatus,
}) => {
  const isActionable = rfqStatus !== 'awarded' && rfqStatus !== 'booked';
  
  return (
    <Card 
      className={`transition-all ${quote.shortlisted ? 'border-2 border-yellow-400 bg-yellow-50/30' : ''} ${quote.status === 'accepted' ? 'border-2 border-green-500 bg-green-50/30' : ''}`}
      data-testid={`quote-card-${quote.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            {isActionable && (
              <Checkbox
                checked={isSelected}
                onCheckedChange={onToggleSelect}
                data-testid={`select-quote-${quote.id}`}
              />
            )}
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">{quote.provider_name}</h3>
                {quote.shortlisted && (
                  <Badge className="bg-yellow-100 text-yellow-700">
                    <Star className="w-3 h-3 mr-1 fill-yellow-500" />
                    Shortlisted
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-500 font-mono">{quote.quote_number}</p>
            </div>
          </div>
          <QuoteStatusBadge status={quote.status} />
        </div>
        
        {/* Quote Amount - Prominent Display */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-4">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Total Quote</p>
              <p className="text-3xl font-bold text-blue-700">
                {currencySymbol}{quote.quote_amount?.toLocaleString()}
              </p>
              {quote.unit_price && (
                <p className="text-sm text-gray-500 mt-1">
                  {currencySymbol}{quote.unit_price}/unit · {quote.quantity_offered} offered
                </p>
              )}
            </div>
            <div className="text-right">
              <DeliveryTimeBadge time={quote.delivery_time} />
              {quote.delivery_included ? (
                <p className="text-sm text-green-600 mt-1">
                  <CheckCircle className="w-3 h-3 inline mr-1" />
                  Delivery Included
                </p>
              ) : quote.delivery_cost > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  +{currencySymbol}{quote.delivery_cost} delivery
                </p>
              )}
            </div>
          </div>
        </div>
        
        {/* Provider Contact */}
        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
          <span className="flex items-center gap-1">
            <Mail className="w-4 h-4" />
            {quote.provider_email}
          </span>
          <span className="flex items-center gap-1">
            <Phone className="w-4 h-4" />
            {quote.provider_phone}
          </span>
        </div>
        
        {/* Validity */}
        <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
          <Calendar className="w-4 h-4" />
          Valid until: {new Date(quote.valid_until).toLocaleDateString()}
        </div>
        
        {/* Notes */}
        {quote.notes && (
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <p className="text-sm text-gray-600">{quote.notes}</p>
          </div>
        )}
        
        {/* Actions */}
        {isActionable && quote.status !== 'rejected' && quote.status !== 'not_selected' && (
          <div className="flex gap-2 pt-3 border-t">
            {!quote.shortlisted ? (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onShortlist(quote.id, true)}
                data-testid={`shortlist-${quote.id}`}
              >
                <Star className="w-4 h-4 mr-1" />
                Shortlist
              </Button>
            ) : (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onShortlist(quote.id, false)}
                data-testid={`unshortlist-${quote.id}`}
              >
                Remove from Shortlist
              </Button>
            )}
            
            <Button 
              size="sm" 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => onAccept(quote)}
              data-testid={`accept-${quote.id}`}
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Accept Quote
            </Button>
            
            <Button 
              variant="outline" 
              size="sm" 
              className="text-red-600 hover:bg-red-50"
              onClick={() => onReject(quote)}
              data-testid={`reject-${quote.id}`}
            >
              <XCircle className="w-4 h-4 mr-1" />
              Reject
            </Button>
          </div>
        )}
        
        {quote.status === 'accepted' && (
          <div className="flex items-center gap-2 pt-3 border-t text-green-700">
            <Award className="w-5 h-5" />
            <span className="font-semibold">This quote has been accepted</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function QuotesInboxPage() {
  const { rfqId } = useParams();
  const navigate = useNavigate();
  const { country } = useCountry();
  
  const [rfqDetails, setRfqDetails] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuotes, setSelectedQuotes] = useState([]);
  const [showShortlistOnly, setShowShortlistOnly] = useState(false);
  const [sortBy, setSortBy] = useState('price_asc');
  
  // Dialogs
  const [acceptDialog, setAcceptDialog] = useState({ open: false, quote: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, quote: null });
  const [bookingDialog, setBookingDialog] = useState({ open: false, quote: null });
  const [rejectReason, setRejectReason] = useState('');
  const [bookingData, setBookingData] = useState({
    delivery_date: '',
    delivery_address: '',
    special_instructions: '',
  });
  
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  
  useEffect(() => {
    const fetchQuotes = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/api/business-services/rfq/${rfqId}/quotes?shortlisted_only=${showShortlistOnly}`
        );
        const data = await response.json();
        
        if (data.success) {
          setRfqDetails({
            rfq_number: data.rfq_number,
            title: data.rfq_title,
            status: data.rfq_status,
            total_quotes: data.total_quotes,
            shortlisted_count: data.shortlisted_count,
          });
          setQuotes(data.quotes || []);
        }
      } catch (error) {
        console.error('Failed to fetch quotes:', error);
        toast.error('Failed to load quotes');
      } finally {
        setLoading(false);
      }
    };
    
    if (rfqId) {
      fetchQuotes();
    }
  }, [rfqId, showShortlistOnly]);
  
  const sortedQuotes = [...quotes].sort((a, b) => {
    switch (sortBy) {
      case 'price_asc':
        return a.quote_amount - b.quote_amount;
      case 'price_desc':
        return b.quote_amount - a.quote_amount;
      case 'delivery':
        const deliveryOrder = { same_day: 0, '1_3_days': 1, '1_week': 2, '2_weeks': 3, custom: 4 };
        return (deliveryOrder[a.delivery_time] || 5) - (deliveryOrder[b.delivery_time] || 5);
      case 'newest':
        return new Date(b.created_at) - new Date(a.created_at);
      default:
        return 0;
    }
  });
  
  const handleShortlist = async (quoteId, shortlist) => {
    try {
      const response = await fetch(
        `${API_URL}/api/business-services/rfq/${rfqId}/quotes/${quoteId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            action: shortlist ? 'shortlist' : 'remove_shortlist' 
          }),
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(shortlist ? 'Quote shortlisted' : 'Removed from shortlist');
        // Refresh quotes
        setQuotes(quotes.map(q => 
          q.id === quoteId 
            ? { ...q, shortlisted: shortlist, status: shortlist ? 'shortlisted' : 'submitted' }
            : q
        ));
      } else {
        toast.error('Failed to update quote');
      }
    } catch (error) {
      console.error('Failed to shortlist:', error);
      toast.error('Failed to update quote');
    }
  };
  
  const handleAcceptQuote = async () => {
    const quote = acceptDialog.quote;
    if (!quote) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/business-services/rfq/${rfqId}/quotes/${quote.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'accept' }),
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Quote accepted! 🎉');
        setAcceptDialog({ open: false, quote: null });
        setBookingDialog({ open: true, quote });
      } else {
        toast.error('Failed to accept quote');
      }
    } catch (error) {
      console.error('Failed to accept:', error);
      toast.error('Failed to accept quote');
    }
  };
  
  const handleRejectQuote = async () => {
    const quote = rejectDialog.quote;
    if (!quote) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/business-services/rfq/${rfqId}/quotes/${quote.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            action: 'reject',
            reason: rejectReason,
          }),
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Quote rejected');
        setRejectDialog({ open: false, quote: null });
        setRejectReason('');
        // Update local state
        setQuotes(quotes.map(q => 
          q.id === quote.id ? { ...q, status: 'rejected' } : q
        ));
      } else {
        toast.error('Failed to reject quote');
      }
    } catch (error) {
      console.error('Failed to reject:', error);
      toast.error('Failed to reject quote');
    }
  };
  
  const handleCreateBooking = async () => {
    const quote = bookingDialog.quote;
    if (!quote) return;
    
    try {
      const params = new URLSearchParams({
        quote_id: quote.id,
        ...(bookingData.delivery_date && { delivery_date: bookingData.delivery_date }),
        ...(bookingData.delivery_address && { delivery_address: bookingData.delivery_address }),
        ...(bookingData.special_instructions && { special_instructions: bookingData.special_instructions }),
      });
      
      const response = await fetch(
        `${API_URL}/api/business-services/rfq/${rfqId}/book?${params}`,
        { method: 'POST' }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Booking ${data.booking_number} created! 🎉`);
        setBookingDialog({ open: false, quote: null });
        navigate(`/bookings/${data.booking_id}`);
      } else {
        toast.error('Failed to create booking');
      }
    } catch (error) {
      console.error('Failed to create booking:', error);
      toast.error('Failed to create booking');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="quotes-inbox-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500 font-mono">{rfqDetails?.rfq_number}</p>
            <h1 className="text-2xl font-bold">{rfqDetails?.title}</h1>
            <p className="text-gray-500">
              {rfqDetails?.total_quotes} quotes received · {rfqDetails?.shortlisted_count} shortlisted
            </p>
          </div>
          <Badge className={
            rfqDetails?.status === 'awarded' ? 'bg-green-100 text-green-700' :
            rfqDetails?.status === 'booked' ? 'bg-purple-100 text-purple-700' :
            'bg-blue-100 text-blue-700'
          }>
            {rfqDetails?.status?.toUpperCase()}
          </Badge>
        </div>
        
        {/* Quick Stats */}
        {quotes.length > 0 && (
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <TrendingDown className="w-6 h-6 mx-auto mb-2 text-green-600" />
                <p className="text-2xl font-bold text-green-700">
                  {currencySymbol}{Math.min(...quotes.map(q => q.quote_amount)).toLocaleString()}
                </p>
                <p className="text-sm text-green-600">Lowest Quote</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 text-center">
                <FileText className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                <p className="text-2xl font-bold text-blue-700">
                  {currencySymbol}{Math.round(quotes.reduce((sum, q) => sum + q.quote_amount, 0) / quotes.length).toLocaleString()}
                </p>
                <p className="text-sm text-blue-600">Average Quote</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4 text-center">
                <Star className="w-6 h-6 mx-auto mb-2 text-yellow-600" />
                <p className="text-2xl font-bold text-yellow-700">
                  {quotes.filter(q => q.shortlisted).length}
                </p>
                <p className="text-sm text-yellow-600">Shortlisted</p>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Filters */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={showShortlistOnly}
                onCheckedChange={setShowShortlistOnly}
              />
              <span className="text-sm">Shortlisted only</span>
            </label>
          </div>
          <select
            className="text-sm border rounded-lg px-3 py-2"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="delivery">Fastest Delivery</option>
            <option value="newest">Newest First</option>
          </select>
        </div>
        
        {/* Quotes List */}
        {sortedQuotes.length > 0 ? (
          <div className="space-y-4">
            {sortedQuotes.map((quote, index) => (
              <div key={quote.id} className="relative">
                {index === 0 && sortBy === 'price_asc' && (
                  <Badge className="absolute -top-2 -left-2 bg-green-500 text-white z-10">
                    Best Price
                  </Badge>
                )}
                <QuoteCard
                  quote={quote}
                  currencySymbol={currencySymbol}
                  isSelected={selectedQuotes.includes(quote.id)}
                  onToggleSelect={() => {
                    setSelectedQuotes(prev => 
                      prev.includes(quote.id)
                        ? prev.filter(id => id !== quote.id)
                        : [...prev, quote.id]
                    );
                  }}
                  onShortlist={handleShortlist}
                  onAccept={(q) => setAcceptDialog({ open: true, quote: q })}
                  onReject={(q) => setRejectDialog({ open: true, quote: q })}
                  rfqStatus={rfqDetails?.status}
                />
              </div>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold mb-2">No Quotes Yet</h3>
            <p className="text-gray-500">
              {showShortlistOnly 
                ? 'No quotes have been shortlisted yet'
                : 'Suppliers are reviewing your RFQ. Quotes will appear here.'}
            </p>
          </Card>
        )}
        
        {/* Accept Confirmation Dialog */}
        <Dialog open={acceptDialog.open} onOpenChange={(open) => setAcceptDialog({ open, quote: open ? acceptDialog.quote : null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Accept Quote</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="mb-4">
                Are you sure you want to accept the quote from <strong>{acceptDialog.quote?.provider_name}</strong> for <strong>{currencySymbol}{acceptDialog.quote?.quote_amount?.toLocaleString()}</strong>?
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700">
                <strong>Note:</strong> Accepting this quote will reject all other quotes and notify the suppliers.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAcceptDialog({ open: false, quote: null })}>
                Cancel
              </Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={handleAcceptQuote}>
                Accept Quote
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Reject Dialog */}
        <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ open, quote: open ? rejectDialog.quote : null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Quote</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <p>
                Reject quote from <strong>{rejectDialog.quote?.provider_name}</strong>?
              </p>
              <div>
                <Label htmlFor="reason">Reason (optional)</Label>
                <Textarea
                  id="reason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Provide feedback for the supplier..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialog({ open: false, quote: null })}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleRejectQuote}>
                Reject Quote
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Booking Dialog */}
        <Dialog open={bookingDialog.open} onOpenChange={(open) => setBookingDialog({ open, quote: open ? bookingDialog.quote : null })}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>🎉 Quote Accepted - Create Booking</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-700 mb-2">
                  <strong>Supplier:</strong> {bookingDialog.quote?.provider_name}
                </p>
                <p className="text-2xl font-bold text-green-700">
                  {currencySymbol}{bookingDialog.quote?.quote_amount?.toLocaleString()}
                </p>
              </div>
              
              <div>
                <Label htmlFor="delivery_date">Preferred Delivery Date</Label>
                <Input
                  id="delivery_date"
                  type="date"
                  value={bookingData.delivery_date}
                  onChange={(e) => setBookingData({ ...bookingData, delivery_date: e.target.value })}
                />
              </div>
              
              <div>
                <Label htmlFor="delivery_address">Delivery Address</Label>
                <Textarea
                  id="delivery_address"
                  value={bookingData.delivery_address}
                  onChange={(e) => setBookingData({ ...bookingData, delivery_address: e.target.value })}
                  placeholder="Enter delivery address..."
                  rows={2}
                />
              </div>
              
              <div>
                <Label htmlFor="instructions">Special Instructions</Label>
                <Textarea
                  id="instructions"
                  value={bookingData.special_instructions}
                  onChange={(e) => setBookingData({ ...bookingData, special_instructions: e.target.value })}
                  placeholder="Any special requirements..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBookingDialog({ open: false, quote: null })}>
                Skip for Now
              </Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={handleCreateBooking}>
                Create Booking
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
